import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Recycle, Menu, X, Calculator, FileText, Building2, LogIn, UserPlus, LayoutDashboard, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: '/', label: 'Pradžia', icon: Recycle },
    { to: '/skaiciuokle', label: 'Skaičiuoklė', icon: Calculator, protected: true },
    { to: '/ppwr', label: 'PPWR', icon: FileText },
  ];

  const userLinks = user ? [
    { to: '/dashboard', label: 'Valdymo skydas', icon: LayoutDashboard },
    { to: '/history', label: 'Istorija', icon: FileText },
    ...(isAdmin() ? [{ to: '/admin', label: 'Admin', icon: Settings }] : []),
  ] : [];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/95 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" onClick={closeMobileMenu}>
            <Recycle className="w-7 h-7 sm:w-8 sm:h-8 text-[#00C300]" />
            <span className="text-base sm:text-lg font-bold text-gray-900">taršosmokestis.lt</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-semibold transition-colors ${
                  isActive(link.to) ? 'text-[#00C300]' : 'text-gray-600 hover:text-[#00C300]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {user && userLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-semibold transition-colors ${
                  isActive(link.to) ? 'text-[#00C300]' : 'text-gray-600 hover:text-[#00C300]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <NotificationBell />
                <span className="text-sm text-gray-600 max-w-[150px] truncate">
                  {user.company_name || user.email}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={logout}
                  className="rounded-full"
                >
                  Atsijungti
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-sm font-semibold text-gray-700 hover:text-[#00C300] rounded-full px-4">
                    Prisijungti
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#00C300] hover:bg-[#00A300] text-white rounded-full px-5 font-semibold">
                    Registruotis
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            {user && <NotificationBell />}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {/* User info for mobile */}
            {user && (
              <div className="px-3 py-3 mb-2 bg-green-50 rounded-lg">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user.company_name || 'Vartotojas'}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            )}

            {/* Navigation Links */}
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={closeMobileMenu}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                  isActive(link.to) 
                    ? 'bg-green-50 text-[#00C300]' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <link.icon className="w-5 h-5" />
                <span className="font-medium">{link.label}</span>
              </Link>
            ))}

            {/* User specific links */}
            {user && (
              <>
                <div className="border-t border-gray-100 my-2"></div>
                {userLinks.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      isActive(link.to) 
                        ? 'bg-green-50 text-[#00C300]' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <link.icon className="w-5 h-5" />
                    <span className="font-medium">{link.label}</span>
                  </Link>
                ))}
              </>
            )}

            {/* Auth buttons for mobile */}
            <div className="border-t border-gray-100 my-2 pt-2">
              {user ? (
                <button
                  onClick={() => { logout(); closeMobileMenu(); }}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-red-600 hover:bg-red-50 w-full transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Atsijungti</span>
                </button>
              ) : (
                <div className="space-y-2">
                  <Link to="/login" onClick={closeMobileMenu} className="block">
                    <Button variant="outline" className="w-full justify-start gap-3 h-12 rounded-lg">
                      <LogIn className="w-5 h-5" />
                      Prisijungti
                    </Button>
                  </Link>
                  <Link to="/register" onClick={closeMobileMenu} className="block">
                    <Button className="w-full justify-start gap-3 h-12 rounded-lg bg-[#00C300] hover:bg-[#00A300]">
                      <UserPlus className="w-5 h-5" />
                      Registruotis
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
